import { ReactNode, useEffect } from 'react';
import styled from 'styled-components';

import xIcon from '../../../../assets/images/icon-x.svg';

export type UIModalFooterAlign = 'end' | 'between' | 'start' | 'center';

const JUSTIFY: Record<UIModalFooterAlign, string> = {
  end: 'flex-end',
  between: 'space-between',
  start: 'flex-start',
  center: 'center',
};

interface UIModalProps {
  // Heading shown top-left. When a plain string it also becomes the dialog's
  // accessible name; pass `ariaLabel` for richer titles.
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
  footerAlign?: UIModalFooterAlign;
  ariaLabel?: string;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  className?: string;
}

// Generic centered dialog: dimmed overlay, white rounded panel, a header with
// a close button, a body, and an optional footer. Closes on overlay click, the
// X, or Escape (each toggleable). Button styling is left to callers — pair the
// footer with `UIButton`. The body carries sensible Montserrat prose defaults
// that callers can override.
export const UIModal = ({
  title,
  onClose,
  children,
  footer,
  width = 498,
  footerAlign = 'end',
  ariaLabel,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  className,
}: UIModalProps) => {
  useEffect(() => {
    if (!closeOnEscape) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, closeOnEscape]);

  const label = ariaLabel ?? (typeof title === 'string' ? title : undefined);

  return (
    <Overlay onMouseDown={closeOnOverlayClick ? onClose : undefined}>
      <Panel
        role="dialog"
        aria-modal="true"
        aria-label={label}
        className={className}
        $width={width}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <Header>
          <h2>{title}</h2>
          <CloseButton type="button" aria-label="Close" onClick={onClose}>
            <img src={xIcon} alt="" />
          </CloseButton>
        </Header>

        <ModalBody>{children}</ModalBody>

        {footer && <Footer $align={footerAlign}>{footer}</Footer>}
      </Panel>
    </Overlay>
  );
};

export default UIModal;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  background: rgba(23, 23, 23, 0.45);
  overflow-y: auto;
`;

// styled-components v6: declare props without the `$` prefix, pass/read with it.
const Panel = styled.div<{ width: number }>`
  width: ${({ $width }) => $width}px;
  max-width: 100%;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
  padding: 29px;
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;

  h2 {
    margin: 0;
    padding: 0;
    font-family: Montserrat, sans-serif;
    font-size: 18px;
    font-weight: 700 !important;
    color: #171717;
    line-height: normal;
  }
`;

const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border: none;
  border-radius: 8px;
  background: #f6f6f6;
  cursor: pointer;
  box-shadow: inset 0 -1px 0.5px rgba(14, 14, 44, 0.18);

  &:hover { background: #ededed; }

  img { width: 16px; height: 16px; }
`;

const ModalBody = styled.div`
  margin-top: 14px;
  font-family: Montserrat, sans-serif;
  font-size: 14px;
  font-weight: 500 !important;
  color: #505050;
  line-height: 24px;

  p { margin: 0; }

  a {
    color: #308de8;
    text-decoration: none;

    &:hover { text-decoration: underline; }
  }
`;

const Footer = styled.div<{ align: UIModalFooterAlign }>`
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => JUSTIFY[$align]};
  gap: 12px;
  margin-top: 24px;
`;
